import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { FileDown, File, Files } from "lucide-react";
import { usePlatformTranslation } from "@/hooks/usePlatformTranslation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export interface ExportDataButtonProps {
  onClick: (exportMode: 'single' | 'multiple') => void;
  directExport?: boolean; // If true, skips dropdown and exports directly
}

/**
 * Export Data button component - exports objects as JSON files or template format.
 * Shows a contextual menu to choose between single file or multiple files export,
 * or directly exports if using a template-based export (like Roblox database).
 */
export function ExportDataButton({ onClick, directExport = false }: ExportDataButtonProps) {
  const { t } = usePlatformTranslation();
  const [open, setOpen] = useState(false);

  const handleExportSingle = () => {
    onClick('single');
    setOpen(false);
  };

  const handleExportMultiple = () => {
    onClick('multiple');
    setOpen(false);
  };

  const handleDirectExport = () => {
    onClick('single'); // Direct export uses 'single' mode (template will handle it)
  };

  // If direct export mode (template-based), show simple button
  if (directExport) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted"
        title={t('platform.common.exportData') || 'Export Data'}
        onClick={handleDirectExport}
      >
        <FileDown className="h-4 w-4" />
      </Button>
    );
  }

  // Otherwise show dropdown menu for JSON export options
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted"
          title={t('platform.common.exportData') || 'Export Data'}
        >
          <FileDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem onClick={handleExportSingle} className="cursor-pointer">
          <File className="mr-2 h-4 w-4" />
          <span>Export as Single JSON</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleExportMultiple} className="cursor-pointer">
          <Files className="mr-2 h-4 w-4" />
          <span>Export as Separate JSONs</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ExportDataButton;
